import { copyTextToClipboard } from './formatProductCatalogStructureForCopy';

const DEFAULT_TEMPLATE_FILENAME = 'capa-template-referencia.jpg';
const DEFAULT_MODULE_FILENAME = 'capa-modulo-referencia.jpg';

function extensionFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    const match = pathname.match(/\.(jpe?g|png|webp|gif)$/i);
    return match ? match[1].toLowerCase() : 'jpg';
  } catch {
    return 'jpg';
  }
}

function withExtension(baseName, url) {
  const ext = extensionFromUrl(url);
  if (baseName.includes('.')) return baseName;
  return `${baseName}.${ext}`;
}

/**
 * Baixa uma imagem de referência no navegador (web admin).
 */
export async function downloadReferenceImage(url, filename = DEFAULT_TEMPLATE_FILENAME) {
  if (!url || typeof document === 'undefined') return false;

  const resolvedFilename = withExtension(filename, url);

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = resolvedFilename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch (error) {
    console.warn('Falha ao baixar imagem de referência via fetch; tentando link direto.', error);

    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = resolvedFilename;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Quais imagens o admin deve anexar na IA da sua preferência (ChatGPT, Claude ou similar) junto com o prompt.
 */
export function getCoverAiAttachmentUrls({
  templateMasterUrl = null,
  moduleCoverUrl = null,
  siblingCount = 0,
  selectedReference = null,
} = {}) {
  if (selectedReference?.url) {
    return getCoverAiAttachmentUrlsFromSelection(selectedReference);
  }

  if (templateMasterUrl && siblingCount > 0) {
    return [
      {
        url: templateMasterUrl,
        filename: DEFAULT_TEMPLATE_FILENAME,
        role: 'template-master',
      },
    ];
  }

  if (moduleCoverUrl) {
    return [
      {
        url: moduleCoverUrl,
        filename: DEFAULT_MODULE_FILENAME,
        role: 'module-cover',
      },
    ];
  }

  if (templateMasterUrl) {
    return [
      {
        url: templateMasterUrl,
        filename: DEFAULT_TEMPLATE_FILENAME,
        role: 'template-master',
      },
    ];
  }

  return [];
}

export function getCoverAiAttachmentUrlsFromSelection(selectedReference) {
  if (!selectedReference?.url) return [];

  const filename =
    selectedReference.kind === 'module-cover' ? DEFAULT_MODULE_FILENAME : DEFAULT_TEMPLATE_FILENAME;

  return [
    {
      url: selectedReference.url,
      filename,
      role: selectedReference.kind || 'template-master',
    },
  ];
}

export async function copyCoverAiAssistantToClipboard({ text, attachmentUrls = [] } = {}) {
  const copied = await copyTextToClipboard(text);
  let downloaded = 0;

  for (const item of attachmentUrls) {
    const ok = await downloadReferenceImage(item.url, item.filename);
    if (ok) downloaded += 1;
  }

  return {
    copied,
    downloaded,
    attachmentCount: attachmentUrls.length,
  };
}

export function buildCoverAiCopyToastMessage({ copied, downloaded, attachmentCount }) {
  if (!copied) {
    return 'Não foi possível copiar. Tente novamente no navegador.';
  }

  if (attachmentCount === 0) {
    return 'Prompt copiado! Cole na IA da sua preferência (ChatGPT, Claude ou similar).';
  }

  if (downloaded > 0) {
    return 'Prompt copiado e imagem de referência baixada! Cole o texto na IA da sua preferência (ChatGPT, Claude ou similar) e anexe a imagem baixada na mesma mensagem.';
  }

  return 'Prompt copiado, mas a imagem não baixou. Salve manualmente a URL do template master e anexe na IA da sua preferência (ChatGPT, Claude ou similar) junto com o texto.';
}

export { DEFAULT_MODULE_FILENAME, DEFAULT_TEMPLATE_FILENAME };
